import { Form, Formik, FormikProps } from 'formik'
import { useCallback, useMemo, useRef, useState } from 'react'
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'react-bootstrap'
import CustomTabMenu from 'src/app/modules/component/CustomTabMenu'
import * as Yup from 'yup'
import { MESSAGE, CODE_GENERATE_FORM } from '../../../../utils/Constant'
import { formatDateDTO } from '../../../../utils/FormatUtils'
import ThongTinRaVien from '../modal-ra-vien/ThongTinRaVien'
import ThongTinChuyenTuyen from './ThongTinChuyenTuyen'
import { toast } from 'react-toastify'

const TAB_KEYS = {
  CHUYEN_TUYEN: '0',
  RA_VIEN: '1',
} as const;

type Props = {
  open: boolean
  handleCloseModal: () => void
  handleSubmit: (values: any) => void
  xuTriData?: any;
  patientInfo?: {
    id: string;
    visitId: string;
    [key: string]: any;
  };
}

const ChuyenTuyenModal = ({
  open,
  handleCloseModal,
  handleSubmit,
  xuTriData,
  patientInfo,
}: Props) => {
  // Nội trú: có patientInfo, hiển thị cả 2 tab
  // Ngoại trú: không có patientInfo, chỉ hiển thị tab Thông tin chuyển tuyến
  const isNoiTru = Boolean(patientInfo);

  const [chuyenTuyenSchema, setChuyenTuyenSchema] = useState<Yup.AnyObjectSchema | null>(null);
  const [raVienSchema, setRaVienSchema] = useState<Yup.AnyObjectSchema | null>(null);
  const [visitedTabs, setVisitedTabs] = useState<Set<string>>(new Set([TAB_KEYS.CHUYEN_TUYEN]));
  const [activeTab, setActiveTab] = useState<string>(TAB_KEYS.CHUYEN_TUYEN);
  const formikRef = useRef<FormikProps<any>>(null);

  const setChuyenTuyenValidation = useCallback((schema: Yup.AnyObjectSchema) => {
    setChuyenTuyenSchema(schema);
  }, []);

  const setRaVienValidation = useCallback((schema: Yup.AnyObjectSchema) => {
    setRaVienSchema(schema);
  }, []);

  const handleTabChange = useCallback((tabKey: string | null) => {
    if (tabKey) {
      setActiveTab(tabKey);
      setVisitedTabs(prev => {
        const newSet = new Set(Array.from(prev));
        newSet.add(tabKey);
        return newSet;
      });
    }
  }, []);

  const requiredTabCount = isNoiTru ? 2 : 1;
  const allTabsVisited = visitedTabs.size >= requiredTabCount;

  const TAB_NAMES: Record<string, string> = {
    [TAB_KEYS.CHUYEN_TUYEN]: 'Thông tin chuyển tuyến',
    [TAB_KEYS.RA_VIEN]: 'Thông tin ra viện',
  };

  const getTabKeyFromErrorField = (errorKey: string): string | null => {
    if (errorKey === CODE_GENERATE_FORM.xuTriThongTinChuyenTuyen) {
      return TAB_KEYS.CHUYEN_TUYEN;
    }
    if (errorKey === CODE_GENERATE_FORM.xuTriRaVien) {
      return TAB_KEYS.RA_VIEN;
    }
    return null;
  };

  const getTabsWithErrors = (errors: Record<string, any>): string[] => {
    const tabsWithErrors: string[] = [];
    Object.keys(errors).forEach(key => {
      const tabKey = getTabKeyFromErrorField(key);
      if (tabKey && !tabsWithErrors.includes(tabKey)) {
        tabsWithErrors.push(tabKey);
      }
    });
    return tabsWithErrors;
  };

  const listTab = useMemo(() => {
    const tabs = [
      {
        title: 'Thông tin chuyển tuyến',
        component: <ThongTinChuyenTuyen patientInfo={patientInfo} setValidationSchema={setChuyenTuyenValidation} />
      },
    ];

    if (isNoiTru) {
      tabs.push({
        title: 'Thông tin ra viện',
        component: <ThongTinRaVien setValidationSchema={setRaVienValidation} />
      });
    }

    return tabs;
  }, [patientInfo, isNoiTru, setChuyenTuyenValidation, setRaVienValidation]);

  const validationSchema = useMemo(() => {
    const chuyenTuyenKey = CODE_GENERATE_FORM.xuTriThongTinChuyenTuyen;

    const baseSchema: Yup.AnyObjectSchema = Yup.object({
      [chuyenTuyenKey]: Yup.object().shape({
        htTime: Yup.mixed().required(MESSAGE.REQUIRED).nullable(),
        htVisitStartDate: Yup.mixed().test({
          name: 'is-start-lower',
          message: 'Ngày bắt đầu phải nhỏ hơn ngày kết thúc',
          test(value, { parent }) {
            if (!parent.htVisitEndDate || !value) return true;
            return new Date(value).getTime() <= new Date(parent.htVisitEndDate).getTime();
          }
        }),
        htVisitEndDate: Yup.mixed().test({
          name: 'is-end-higher',
          message: 'Ngày kết thúc phải lớn hơn ngày bắt đầu',
          test(value, { parent }) {
            if (!parent.htVisitStartDate || !value) return true;
            return new Date(value).getTime() >= new Date(parent.htVisitStartDate).getTime();
          }
        }),
      })
    });

    const schemas = [baseSchema, chuyenTuyenSchema];
    if (isNoiTru) {
      schemas.push(raVienSchema);
    }

    return (schemas.filter(Boolean) as Yup.AnyObjectSchema[]).reduce((acc, schema) => acc.concat(schema), Yup.object());
  }, [chuyenTuyenSchema, raVienSchema, isNoiTru]);

  const handleFormSubmit = (values: any) => {
    const chuyenTuyenData = values?.[CODE_GENERATE_FORM.xuTriThongTinChuyenTuyen];
    const raVienData = isNoiTru ? values?.[CODE_GENERATE_FORM.xuTriRaVien] : {};

    const submitData = {
      ...values,
      // Dữ liệu từ tab Thông tin chuyển tuyến
      ...chuyenTuyenData,
      htVisitStartDate: formatDateDTO(chuyenTuyenData?.htVisitStartDate),
      htVisitEndDate: formatDateDTO(chuyenTuyenData?.htVisitEndDate),
      htFor: chuyenTuyenData?.htFor?.name,
      htForCode: chuyenTuyenData?.htFor?.code,
      htModality: chuyenTuyenData?.htModality?.name,
      htModalityCode: chuyenTuyenData?.htModality?.code,
      htReason: chuyenTuyenData?.htReason?.name,
      htReasonCode: chuyenTuyenData?.htReason?.code,
      htReasonDoctor: chuyenTuyenData?.htReasonDoctor?.name,
      htReasonDoctorCode: chuyenTuyenData?.htReasonDoctor?.code,
      htHospitalCode: chuyenTuyenData?.htHospital?.bvInsCode,
      htHospitalName: chuyenTuyenData?.htHospital?.bvName,
      htCompliant: Boolean(chuyenTuyenData?.htCompliant),
      htNonCompliant: Boolean(chuyenTuyenData?.htNonCompliant),
      // Dữ liệu từ tab Thông tin ra viện (chỉ nội trú)
      ...raVienData,
    };

    delete submitData?.htHospital;
    delete submitData?.[CODE_GENERATE_FORM.xuTriThongTinChuyenTuyen];

    handleSubmit(submitData);
  };

  const handleClickSubmit = async () => {
    if (!allTabsVisited) {
      toast.warning(`Vui lòng kiểm tra lại Thông tin ra viện`);
      return;
    }

    const formik = formikRef.current;
    if (!formik) return;

    const errors = await formik.validateForm();

    if (Object.keys(errors).length > 0) {
      // Set touched cho tất cả fields có lỗi
      const touchedFields: Record<string, any> = {};
      Object.keys(errors).forEach(key => {
        const errorValue = errors[key];
        if (errorValue && typeof errorValue === 'object' && !Array.isArray(errorValue)) {
          touchedFields[key] = {};
          Object.keys(errorValue as object).forEach(subKey => {
            touchedFields[key][subKey] = true;
          });
        } else {
          touchedFields[key] = true;
        }
      });
      formik.setTouched(touchedFields, false);

      // Hiển thị thông báo các tab có lỗi
      const tabsWithErrors = getTabsWithErrors(errors);
      if (tabsWithErrors.length > 0) {
        const errorTabNames = tabsWithErrors.map(tab => TAB_NAMES[tab]);
        toast.warn(`Vui lòng kiểm tra lại ${errorTabNames.join(', ')}`);
      }
      return;
    }

    formik.submitForm();
  };

  return (
    <Modal
      centered
      show={open}
      dialogClassName='modal-xl'
      size='xl'
      onHide={handleCloseModal}
      scrollable
    >
      <Formik<any>
        innerRef={formikRef}
        initialValues={{
          htDiagnosis: xuTriData?.icdXPrimary,
          htDiagnosisName: xuTriData?.icdXPrimaryName,
          htComorbidity: xuTriData?.comorbidity,
        }}
        onSubmit={handleFormSubmit}
        validationSchema={validationSchema}
      >
        <Form>
          <ModalHeader closeButton className='py-4'>
            <h4>Cập nhật phiếu chuyển tuyến</h4>
          </ModalHeader>

          <ModalBody className='spaces pt-0 pr-5'>
            <CustomTabMenu
              danhsachTabs={listTab}
              activeTab={activeTab}
              onTabChange={handleTabChange}
            />
          </ModalBody>

          <ModalFooter className='d-flex justify-content-end py-2'>
            <Button className='btn-fill spaces min-w-80' type='button' onClick={handleClickSubmit}>Lưu</Button>
          </ModalFooter>
        </Form>
      </Formik>
    </Modal>
  )
}

export default ChuyenTuyenModal